import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { getDLToken, ensureDLToken } from "@/lib/dlToken";
import { Camera, FileSignature, MessageSquare, DollarSign, Clock } from "lucide-react";
import { fmtDateTime } from "@/lib/datetime";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fileUrl = (id) => `${BACKEND_URL}/api/files/${id}?auth=${encodeURIComponent(getDLToken())}`;

/**
 * Proof-of-Delivery viewer.
 * Renders signature + up to 2 photos + driver comment + copay state for a single delivered order.
 * Loads `/orders/{id}` on open so it always shows the latest server state.
 */
export default function PODViewDialog({ orderId, open, onClose }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !orderId) {
      setOrder(null);
      return;
    }
    setLoading(true);
    // Make sure a short-lived download token exists before we render the
    // <img src> tags built with `getDLToken()` below.
    ensureDLToken().catch(() => {});
    api.get(`/orders/${orderId}`)
      .then((r) => setOrder(r.data))
      .finally(() => setLoading(false));
  }, [orderId, open]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pod-view-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="h-5 w-5 text-primary" />
            Proof of Delivery
          </DialogTitle>
        </DialogHeader>

        {loading && <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>}

        {!loading && order && (() => {
          // Marquez-specific POD layout: when the order has multiple
          // prescription line-items (name starts with "RX "/"Rx "), show
          // one small cropped signature ON TOP OF a signature line for
          // EACH Rx — the customer's single captured signature acknowledges
          // each Rx individually, so the pharmacy has a per-Rx receipt.
          const pharmacyName = (order.pharmacy_name || "").toLowerCase();
          const isMarquez = pharmacyName.includes("marquez");
          const rxItems = (order.items || []).filter((it) =>
            /^rx\s/i.test(String(it?.name || "").trim())
          );
          const useRxSignatureLayout = isMarquez && rxItems.length > 0 && !!order.pod_signature_id;
          return (
          <div className="space-y-4">
            {/* Header line */}
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
              <span className="font-semibold text-base">{order.customer_name}</span>
              {order.order_number && (
                <span className="font-mono-num text-xs text-muted-foreground">#{order.order_number}</span>
              )}
              {order.delivered_at && (
                <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" /> Delivered {fmtDateTime(order.delivered_at)}
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground">{order.address}</div>

            {/* Driver comment */}
            {order.pod_comment ? (
              <div className="rounded-xl border border-border bg-secondary/40 p-3" data-testid="pod-view-comment">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1 flex items-center gap-1">
                  <MessageSquare className="h-3 w-3" /> Driver comment
                </div>
                <div className="text-sm">{order.pod_comment}</div>
              </div>
            ) : (
              <div className="text-xs text-muted-foreground italic">No driver comment captured.</div>
            )}

            {/* Copay */}
            {typeof order.copay === "number" && order.copay > 0 && (
              <div
                className={`rounded-xl border p-3 ${
                  order.copay_collected
                    ? "border-[#16A34A]/40 bg-[#dcfce7] text-[#166534]"
                    : "border-[#DC2626]/40 bg-[#fee2e2] text-[#991B1B]"
                }`}
                data-testid="pod-view-copay"
              >
                <div className="flex items-center gap-2 text-sm font-medium">
                  <DollarSign className="h-4 w-4" />
                  Copay ${order.copay.toFixed(2)} —{" "}
                  {order.copay_collected ? "collected (cash)" : "not collected"}
                </div>
              </div>
            )}

            {/* Signature — Marquez per-Rx layout OR standard single signature */}
            {useRxSignatureLayout ? (
              <div data-testid="pod-view-rx-signatures">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5 flex items-center gap-1">
                  <FileSignature className="h-3 w-3" /> Prescriptions received &amp; signed
                </div>
                <div className="rounded-lg border border-border bg-white divide-y divide-border">
                  {rxItems.map((it, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 px-3 py-2"
                      data-testid={`pod-view-rx-row-${i}`}
                    >
                      <div className="w-32 shrink-0 font-mono-num text-sm text-foreground">
                        {String(it.name).trim()}
                      </div>
                      {/* Signature line with the cropped signature sitting on top */}
                      <div className="relative flex-1 h-10">
                        <img
                          src={fileUrl(order.pod_signature_id)}
                          alt={`Signature for ${it.name}`}
                          className="absolute left-1 bottom-1 h-9 object-contain"
                          style={{ maxWidth: "100%" }}
                          data-testid={`pod-view-rx-signature-${i}`}
                        />
                        <div className="absolute left-0 right-0 bottom-0 border-b border-foreground/70" />
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1.5">
                  Patient&apos;s signature is reproduced on the line next to each prescription number as receipt of delivery.
                </p>
              </div>
            ) : (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5 flex items-center gap-1">
                  <FileSignature className="h-3 w-3" /> Signature
                </div>
                {order.pod_signature_id ? (
                  <img
                    src={fileUrl(order.pod_signature_id)}
                    alt="Signature"
                    className="w-full rounded-lg border border-border bg-white object-contain max-h-64"
                    data-testid="pod-view-signature"
                  />
                ) : (
                  <div className="rounded-lg border border-dashed border-border bg-secondary/30 p-6 text-center text-xs text-muted-foreground">
                    No signature captured
                  </div>
                )}
              </div>
            )}

            {/* Photos */}
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5 flex items-center gap-1">
                <Camera className="h-3 w-3" /> Photos
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[order.pod_photo_id, order.pod_photo_id_2].map((pid, i) => (
                  <div key={i}>
                    {pid ? (
                      <a href={fileUrl(pid)} target="_blank" rel="noopener noreferrer">
                        <img
                          src={fileUrl(pid)}
                          alt={`POD photo ${i + 1}`}
                          className="w-full h-48 object-cover rounded-lg border border-border"
                          data-testid={`pod-view-photo-${i + 1}`}
                        />
                      </a>
                    ) : (
                      <div className="h-48 rounded-lg border border-dashed border-border bg-secondary/30 grid place-items-center text-xs text-muted-foreground">
                        No photo {i + 1}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {order.pod_photo_id_3 && (
                <div className="mt-3">
                  <div className="text-[10px] uppercase tracking-wider text-destructive font-semibold mb-1.5 flex items-center gap-1.5">
                    <span className="inline-flex items-center px-1 py-0.5 rounded bg-destructive text-destructive-foreground text-[9px] font-bold uppercase">CII</span>
                    Recipient ID photo
                  </div>
                  <a href={fileUrl(order.pod_photo_id_3)} target="_blank" rel="noopener noreferrer">
                    <img
                      src={fileUrl(order.pod_photo_id_3)}
                      alt="Recipient ID photo"
                      className="w-full h-48 object-cover rounded-lg border border-destructive/30"
                      data-testid="pod-view-photo-3"
                    />
                  </a>
                </div>
              )}
            </div>
          </div>
          );
        })()}
      </DialogContent>
    </Dialog>
  );
}
